import { useCallback, useEffect, useState } from "react";

import EditNameModal from "./EditName/EditName";
import EditPasswordModal from "./EditPassword/EditPassword";
import useSize from "../../useSize/useSize";
import StatusButton from "./StatusButton/StatusButton";
import ResetPassword from "./StatusButton/ResetPassword";
import ActionButton from "./ActionButton/ActionButton";
import toast from "react-hot-toast";
import Backend from "../../../Backend/Backend";
import Loading from "../../Loading/Loading";

const backend = await new Backend().init();

export default function Edit({ user_id }) {
  const windowSize = useSize();

  const [user, setUser] = useState(null);
  const [isSuper, setSuper] = useState({});
  const [loading, setLoading] = useState(true);

  // Modals
  const [isNameModalOpen, setIsNameModalOpen] = useState(false);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);

  const closeAllModals = () => {
    setIsNameModalOpen(false);
    setIsPasswordModalOpen(false);
  };

  /** --------------------------
   * LOAD USER DATA
   ---------------------------*/
  const loadUser = useCallback(async () => {
    setLoading(true);

    try {
      const payload = {
        server: localStorage.getItem("server"),
        id: localStorage.getItem("id"),
        username: localStorage.getItem("username"),
        token: localStorage.getItem("token"),
        user_id,
      };

      const r = await backend.load_user_select(payload);
      if (r?.error === "False") {
        setUser(r.user || {});
        setSuper(r.userdata || {});
        setLoading(false);
      } else {
        setLoading(false);
        toast.error(r?.message || "Failed to load user");
      }
    } catch (err) {
      setLoading(false);
      console.error(err);
      toast.error("Error loading user");
    }
  }, [user_id]);

  useEffect(() => {
    if (user_id) loadUser();
  }, [user_id, loadUser]);

  // ESC to close modals
  useEffect(() => {
    const onEsc = (e) => {
      if (e.key === "Escape") closeAllModals();
    };
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, []);

  const containerHeight = windowSize[1] - 170;

  /** --------------------------
   * WHEN USER ID IS MISSING
   ---------------------------*/
  if (!user_id) {
    return (
      <div className="p-6 m-4 bg-red-50 border border-red-200 rounded-xl text-center shadow">
        <h2 className="text-red-600 font-bold text-lg">⚠️ Error</h2>
        <p>User ID is required.</p>
      </div>
    );
  }

  /** --------------------------
   * LOADING STATE
   ---------------------------*/
  if (loading || !user) {
    return (
      <div className="loadingContainer">
        <Loading height={containerHeight} />
      </div>
    );
  }

  /** --------------------------
   * MAIN UI
   ---------------------------*/

  const { id, name, username, trade, only_sq, status } = user;

  return (
    <div
      className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2 pl-5 pr-5 pb-5"
      style={{ height: containerHeight, width: windowSize[0] - 100 }}
    >
      {/* ------------------------------------
          COLUMN 1 — USER DETAILS
      --------------------------------------*/}
      <div
        style={{
          overflowY: "scroll",
          height: containerHeight - 20,
          paddingBottom: 200,
        }}
        className="bg-[var(--background)] rounded-xl p-5 pb-0 shadow-inner border-2 border-[var(--hover)]"
      >
        <div className="space-y-2 text-sm">
          <DetailRow label="USERNAME" value={username} />

          <DetailRow
            label="NAME"
            value={name}
            buttonLabel="EDIT"
            onButtonClick={() => setIsNameModalOpen(true)}
          />

          <DetailRow
            label="PASSWORD"
            value="********"
            buttonLabel="EDIT"
            onButtonClick={() => setIsPasswordModalOpen(true)}
          />
        </div>

        {/* USER STATUS BUTTONS */}
        <div className="flex flex-wrap gap-2 mt-5">
          <ResetPassword user_id={id} />
          {[
            ["ACTIVE", "status", status],
            ["TRADE", "trade", trade],
            ["ONLY SQ", "only_sq", only_sq],
          ].map(([label, key, val]) => (
            <StatusButton
              key={key}
              label={label}
              user_id={user_id}
              user_label={key}
              user_status={val}
            />
          ))}
        </div>
      </div>

      {/* ------------------------------------
          COLUMN 2 — USER EXCHANGES + SETTINGS
      --------------------------------------*/}
      <div
        style={{
          overflowY: "scroll",
          height: containerHeight - 20,
          paddingBottom: 200,
        }}
        className="bg-[var(--background)] rounded-xl p-5 pb-0  shadow-inner border-2 border-[var(--hover)]"
      >
        <h3 className="font-bold text-[var(--text)] text-lg mb-3">
          🌐 User Exchanges
        </h3>

        <SectionChips
          reload={loadUser}
          user={user}
          chips={[
            { key: "nse", label: "Nse Future", status: isSuper.nse },
            { key: "nse_cash", label: "Nse Equity", status: isSuper.nse_cash },
            { key: "options", label: "Nse Options", status: isSuper.options },
            { key: "mcx", label: "Mcx Future", status: isSuper.mcx },
            { key: "mcx_options", label: "Mcx Options", status: isSuper.mcx },
            {
              key: "comex",
              label: "COMEX",
              status: isSuper.comex,
            },
            {
              key: "crypto",
              label: "Crypto",
              status: isSuper.crypto,
            },
            {
              key: "forex",
              label: "Forex",
              status: isSuper.forex,
            },
            { key: "sgx", label: "SGX", status: isSuper.sgx },
            { key: "us_stock", label: "US Stock", status: isSuper.us_stock },
            { key: "index", label: "Others", status: isSuper.index },
            { key: "dgcx", label: "Dgcx", status: isSuper.dgcx },
            { key: "ncdex", label: "Ncdex", status: isSuper.ncdex },
          ]}
        />

        <h3 className="font-bold text-lg text-[var(--text)] mt-5 mb-3">
          ⚙️ User Other
        </h3>

        {[
          ["SQ-OFF TIME", "sq_time"],
          ["LIMIT %", "limit_p"],
          ["PROFIT LIMIT", "profit_limit"],
          ["STANDING LIMIT", "standing_limit"],
          ["MIN RATE", "min_rate"],
          ["TOTAL MCX LOT", "max_lot"],
        ].map(([label, key]) => (
          <div
            key={key}
            className="flex justify-between items-center border-b-2 border-[var(--hover)] pb-1 text-sm"
          >
            <span className="font-bold  bg-[var(--background)] text-[var(--text)] ">
              {label}: {user[key]}
            </span>

            <ActionButton
              label="SUBMIT"
              item={key}
              user={user}
              reload={loadUser}
            />
          </div>
        ))}
      </div>
      {/* ------------------------------------
          MODALS
      --------------------------------------*/}
      <EditNameModal
        open={isNameModalOpen}
        onClose={closeAllModals}
        user={user}
        reload={loadUser}
      />
      <EditPasswordModal
        open={isPasswordModalOpen}
        onClose={closeAllModals}
        user={user}
        reload={loadUser}
      />
    </div>
  );
}

/* -------------------------------------------------- */
/* COMPONENTS */
/* -------------------------------------------------- */

function DetailRow({ label, value, buttonLabel, onButtonClick }) {
  return (
    <div className="flex justify-between border-b-2 border-[var(--hover)] pb-1 items-center">
      <span className="font-bold text-[var(--text)] ">{label}</span>
      <div className="flex items-center gap-2">
        <span className="font-bold text-[var(--text)] ">{value}</span>

        {buttonLabel && (
          <button
            onClick={onButtonClick}
            className="px-2 py-0.5 bg-[var(--primary)] text-white rounded-full text-xs font-semibold"
          >
            {buttonLabel}
          </button>
        )}
      </div>
    </div>
  );
}

function SectionChips({ chips, user, reload }) {
  return (
    <div className="flex flex-wrap gap-2">
      {chips.map((chip) =>
        chip.status === 1 ? (
          <StatusButton
            key={chip.key}
            label={chip.label}
            user_id={user.id}
            user_label={chip.key}
            user_status={user[chip.key]}
            fetchUsers={reload}
            reload={1}
          />
        ) : null
      )}
    </div>
  );
}

function EditRow({
  multipleKey,
  exchange,
  user,
  label1,
  label2,
  label3,
  label4,
  label5,
  broker_id,
  sub_broker_id,
  type,
}) {
  // Determine grid classes based on broker_id and sub_broker_id
  const getGridClasses = () => {
    const hasBroker = broker_id !== 0;
    const hasSubBroker = sub_broker_id !== 0;

    if (hasBroker && hasSubBroker) {
      return "grid-cols-6 gap-6";
    } else if (hasBroker) {
      return "grid-cols-5 gap-5";
    } else if (hasSubBroker) {
      return "grid-cols-5 gap-5"; // If only sub_broker exists, still use 4 columns
    } else if (exchange === "mcx") {
      return "grid-cols-5 gap-5";
    } else {
      return "grid-cols-4 gap-4";
    }
  };

  const gridClasses = getGridClasses();

  return (
    <div className={`grid ${gridClasses} text-xs font-semibold`}>
      <div>
        <span className="text-[var(--subtext)] text-[10px]">MULTIPLE</span>
        <div className="font-bold text-[var(--text)]">{multipleKey || 0}</div>
      </div>
      <div>
        <span className="text-[var(--subtext)] text-[10px]">MARGIN</span>
        <div className="font-bold text-[var(--text)]">{label1}</div>
      </div>
      <div>
        <span className="text-[var(--subtext)] text-[10px]">MARGIN-H</span>
        <div className="font-bold text-[var(--text)]">{label2}</div>
      </div>

      {exchange === "mcx" ? (
        <>
          <div>
            <span className="text-[var(--subtext)] text-[10px]">COMM-LOT</span>
            <div className="font-bold text-[var(--text)]">
              {exchange === "comex" ||
              exchange === "crypto" ||
              exchange === "forex" ||
              exchange === "sgx" ||
              exchange === "us_stock" ||
              exchange === "index"
                ? "$"
                : "₹"}{" "}
              {user.b_mcx_lot} {type}
            </div>
          </div>
          <div>
            <span className="text-[var(--subtext)] text-[10px]">COMM-VOL</span>
            <div className="font-bold text-[var(--text)]">
              {exchange === "comex" ||
              exchange === "crypto" ||
              exchange === "forex" ||
              exchange === "sgx" ||
              exchange === "us_stock" ||
              exchange === "index"
                ? "$"
                : "₹"}{" "}
              {user.b_mcx_volume} {type}
            </div>
          </div>
        </>
      ) : (
        <div>
          <span className="text-[var(--subtext)] text-[10px]">COMMISSION</span>
          <div className="font-bold text-[var(--text)]">
            {exchange === "comex" ||
            exchange === "crypto" ||
            exchange === "forex" ||
            exchange === "sgx" ||
            exchange === "us_stock" ||
            exchange === "index"
              ? "$"
              : "₹"}{" "}
            {label3} {type}
          </div>
        </div>
      )}

      {broker_id !== 0 && (
        <div>
          <span className="text-[var(--subtext)] text-[10px]">BROKER</span>
          <div className="font-bold text-[var(--text)]">
            {exchange === "mcx"
              ? user.lot_volume === 0
                ? label4
                : parseFloat(label4).toFixed(2)
              : label4}{" "}
            {type}
          </div>
        </div>
      )}

      {sub_broker_id !== 0 && (
        <div>
          <span className="text-[var(--subtext)] text-[10px]">SUB BROKER</span>
          <div className="font-bold text-[var(--text)]">
            {label5} {type}
          </div>
        </div>
      )}
    </div>
  );
}
